import {
  CEILING_Y,
  FLOOR_Y,
  PLAYER_SIZE,
  PLAYER_X,
  PLAYFIELD_HEIGHT,
  PLAYFIELD_WIDTH,
  SCROLL_SPEED_PX_PER_BEAT,
} from '@shared/gameTypes';
import { drawActiveBurstOverlay, drawBurstChamberBands } from './render/pulseDrawBurst';
import { drawHud } from './render/pulseDrawHud';
import { drawCountdown, drawFlashes, drawFloatingTexts, drawTapHint } from './render/pulseDrawOverlays';
import {
  drawApproachRing,
  drawDownbeatTick,
  drawPads,
  drawReceptorBand,
} from './render/pulseDrawPads';
import { PlayerRenderer } from './render/pulseDrawPlayer';
import { SceneRenderer } from './render/pulseDrawScene';
import { drawGoal, drawHazards, drawSectionBands, drawSectionLabels } from './render/pulseDrawTrack';
import { ParticleSystem } from './render/pulseParticles';
import type {
  FlashSpec,
  FloatingText,
  ParticleEmission,
  RenderInput,
  ShakeSpec,
} from './render/pulseRenderTypes';
import { getRenderQuality, type RenderQuality } from './renderQuality';

export type { FlashSpec, FloatingText, ParticleEmission, RenderInput, ShakeSpec };

/**
 * Thin orchestrator for the Pulse renderer. Owns the canvas + DPR
 * bookkeeping, a couple of small stateful subsystems (stars, particles,
 * player arc tween), and the per-frame draw order. All heavyweight draw
 * code lives in `./render/*` so this file stays small enough to read top
 * to bottom and the per-subsystem files can be edited in isolation.
 */
export class GameRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private scene: SceneRenderer;
  private particles: ParticleSystem;
  private player = new PlayerRenderer();
  private dpr = 1;
  private lastFrameAt = performance.now();
  private viewWidth = PLAYFIELD_WIDTH;
  private viewHeight = PLAYFIELD_HEIGHT;
  // Cached at construction. We accept a stale value if the user toggles
  // their OS setting mid-session — re-mounting the page picks up the
  // change. The alternative (a live media listener) leaks unless we add
  // explicit cleanup and isn't worth the complexity for a setting that
  // almost never changes mid-game.
  private reducedMotion = false;
  private quality: RenderQuality;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      throw new Error('Failed to acquire 2d canvas context');
    }
    this.ctx = ctx;
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    this.quality = getRenderQuality();
    this.scene = new SceneRenderer(this.quality);
    this.particles = new ParticleSystem(this.reducedMotion, this.quality);
    this.resize();
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprCap);
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(320, Math.floor(rect.width));
    const h = Math.max(180, Math.floor(rect.height));
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.dpr = dpr;
    this.viewWidth = w;
    this.viewHeight = h;
  }

  /**
   * The DPR used to back the canvas. Capped via the render-quality
   * tier (see `renderQuality.ts` — 2 on desktop, 1.5 on phones).
   * Callers that convert playfield → CSS px should use this, not
   * `window.devicePixelRatio`, because the canvas was sized using
   * this capped value.
   */
  getRenderDpr(): number {
    return this.dpr;
  }

  getViewSize(): { width: number; height: number } {
    return { width: this.viewWidth, height: this.viewHeight };
  }

  emit(emission: ParticleEmission): void {
    this.particles.emit(emission);
  }

  draw(input: RenderInput): void {
    if (
      Math.floor(input.width) !== this.viewWidth ||
      Math.floor(input.height) !== this.viewHeight
    ) {
      this.resize();
    }

    const now = performance.now();
    const dt = Math.min(0.05, (now - this.lastFrameAt) / 1000);
    this.lastFrameAt = now;

    for (const emission of input.particleEmissions) {
      this.particles.emit(emission);
    }
    input.particleEmissions.length = 0;

    const ctx = this.ctx;
    const w = this.viewWidth;
    const h = this.viewHeight;

    // Reduced-motion users get no shake; everyone else gets the full
    // effect. Shake is purely cosmetic and is the most jarring single
    // thing we draw, so it's the highest-impact accessibility win.
    const shakeAmount = this.reducedMotion ? 0 : input.shake.amount;
    const shakeX = shakeAmount > 0.01 ? (Math.random() - 0.5) * shakeAmount * 2 : 0;
    const shakeY = shakeAmount > 0.01 ? (Math.random() - 0.5) * shakeAmount * 2 : 0;

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.translate(shakeX, shakeY);

    this.scene.drawBackground(ctx, input.chart, w, h, input.beatPulse);
    this.scene.drawStars(ctx, w, h, dt, input.paused ? 0 : 1);

    const scaleX = w / PLAYFIELD_WIDTH;
    const scaleY = h / PLAYFIELD_HEIGHT;
    const playerX = PLAYER_X * scaleX;
    const floorY = FLOOR_Y * scaleY;
    const ceilingY = CEILING_Y * scaleY;
    const padSize = PLAYER_SIZE * Math.min(scaleX, scaleY);

    this.scene.drawRails(ctx, input.chart, ceilingY, floorY, w, input.beatPulse);

    const beat = input.beat;
    const xPerBeat = SCROLL_SPEED_PX_PER_BEAT * scaleX;

    // Receptor band sits behind everything in the lane so pads/hazards/
    // labels overlay on top. The label only renders alongside the
    // first-run TAP SPACE hint so experts aren't visually nagged.
    drawReceptorBand(
      ctx,
      input.chart,
      playerX,
      ceilingY,
      floorY,
      padSize,
      input.beatPulse,
      input.showTapHint ?? false,
    );

    drawHazards(ctx, input.chart, beat, playerX, ceilingY, floorY, xPerBeat, scaleY, w);
    drawSectionBands(ctx, input.chart, beat, playerX, ceilingY, floorY, xPerBeat, w);
    drawBurstChamberBands(
      ctx,
      input.chart,
      input.state,
      beat,
      playerX,
      ceilingY,
      floorY,
      xPerBeat,
      w,
    );
    drawPads(
      ctx,
      input.chart,
      input.state,
      beat,
      playerX,
      ceilingY,
      floorY,
      xPerBeat,
      padSize,
      w,
    );
    drawSectionLabels(ctx, input.chart, beat, playerX, ceilingY, xPerBeat, w, h);
    drawGoal(ctx, input.chart, beat, playerX, ceilingY, floorY, xPerBeat, w);

    drawApproachRing(ctx, input.chart, input.state, beat, playerX, ceilingY, floorY, padSize);
    drawDownbeatTick(ctx, input.chart, beat, playerX, ceilingY, floorY, padSize);
    this.player.drawGhostCube(
      ctx,
      input.chart,
      input.state,
      beat,
      playerX,
      ceilingY,
      floorY,
      padSize,
      input.flipAnim,
    );
    this.player.drawPlayer(ctx, input.state, playerX, ceilingY, floorY, padSize, input.flipAnim);

    this.particles.update(dt);
    this.particles.draw(ctx);

    drawActiveBurstOverlay(ctx, input.chart, input.state, beat, w, ceilingY, floorY);

    drawFlashes(ctx, input.flashes, w, h, dt);
    drawFloatingTexts(ctx, input.floatingTexts, dt);
    if (input.showTapHint) {
      drawTapHint(ctx, input.chart, input.state, beat, playerX, ceilingY, floorY, xPerBeat, w);
    }
    drawCountdown(ctx, input.countdown, w, h);
    drawHud(ctx, input.state, beat, input.chart, w);

    ctx.restore();
  }
}
